import { useState } from "react";
import { createPortal } from "react-dom";
import { extractInvite } from "@veto/domain";
import { ModalShell } from "./ModalShell";
import { useQrCamera } from "./useQrCamera";

/** Scan someone's invite QR (FR-CON-001). Anything that isn't a Veto invite is ignored. */
export function InviteScannerModal({
  onFound,
  onClose,
}: {
  onFound: (inviteText: string) => void;
  onClose: () => void;
}) {
  const [round, setRound] = useState(0);
  const [foreign, setForeign] = useState(false);
  const { videoRef, status } = useQrCamera((text) => {
    if (!extractInvite(text)) {
      setForeign(true);
      return false; // keep scanning — maybe the wrong code was in frame
    }
    onFound(text);
    return true;
  }, round);

  const modal = (
    <ModalShell title="Scan invite code" ariaLabel="Scan invite QR code" onClose={onClose} size="sm" className="scanner-modal">
      <div className="stack">
        <p className="muted" style={{ margin: 0 }}>
          Ask them to open <strong>New chat → Show my QR code</strong>, then point your camera at their screen.
        </p>
        {status.kind !== "error" && (
          <div className="scanner-frame">
            <video ref={videoRef} muted autoPlay playsInline />
            <div className="scanner-reticle" />
          </div>
        )}
        {(status.kind === "starting" || status.kind === "scanning") && (
          <p className="scan-status muted" role="status" style={{ margin: 0 }}>
            {status.kind === "starting"
              ? "Starting camera…"
              : foreign
                ? "That code isn't a Veto invite — keep their invite QR in view."
                : "Looking for the code… hold steady, fill the frame."}
          </p>
        )}
        {status.kind === "error" && (
          <p className="scan-status" role="alert" style={{ margin: 0, color: "var(--danger, #c44)" }}>
            {status.message} You can also paste the link they sent you.
          </p>
        )}
        <div className="actions-row">
          <button className="btn secondary" type="button" onClick={onClose}>
            Cancel
          </button>
          {status.kind === "error" && (
            <button className="btn" type="button" onClick={() => setRound((r) => r + 1)}>
              Try again
            </button>
          )}
        </div>
      </div>
    </ModalShell>
  );
  if (typeof document === "undefined") return modal;
  return createPortal(modal, document.body);
}
